import {parseAbsoluteToLocal} from '@internationalized/date';
import { Component } from './FormDefinitions';
import { Checkbox, ComboBox, DatePicker, Flex, Item, ListView, NumberField, Text, TextArea, TextField, View } from '@adobe/react-spectrum';
import { SoapUtils } from '../../../../../utils/SoapUtils';
import FolderDropDown from '../../../data-components/FolderDropDown';
import { ItemDropDownFormControl } from '../../../data-components/ItemDropDown';
import { UserDropDownFormControl } from '../../../data-components/UserDropDown';
import styles from './FormControl.module.css';

export interface IFormControlProps {
    component: Component;
    updateData: (key: string, value: any) => void;
    getData: (key: string, default_value:any) => any;
    onFieldFocus?: (key: string) => void;
    onFieldBlur?: (key: string) => void;
}

export interface IFormControlMarkMandatoryProps extends IFormControlProps {
    markMandatory: (key: string) => void;
}
const warning_styles = {
    color: 'red',
    fontSize: '12px',
    fontWeight: 'bold'
}

const CONTROLS = {
    mx:{
        controls:{
            Label: 'mx.controls::Label',
            List: 'mx.controls::List',
        },
        containers:{
            Canvas: 'mx.containers::Canvas',
        }
    },
    com:{
        atollon:{
            formDesigner:{
                components:{
                    MainContentGroup: "com.atollon.formDesigner.components::MainContentGroup",
                    VBoxExtended: "com.atollon.formDesigner.components::VBoxExtended",
                    HBoxExtended: "com.atollon.formDesigner.components::HBoxExtended",
                    inputs: {
                        FormTextInput: "com.atollon.formDesigner.components.inputs::FormTextInput",
                        FormDateField: "com.atollon.formDesigner.components.inputs::FormDateField",
                        FormNumberInput: "com.atollon.formDesigner.components.inputs::FormNumberInput",
                        FormComboBox: "com.atollon.formDesigner.components.inputs::FormComboBox",
                        FormTextArea: "com.atollon.formDesigner.components.inputs::FormTextArea",
                        FormCheckbox: "com.atollon.formDesigner.components.inputs::FormCheckbox"
                    }
                }
            },
            gui:{
                FolderContextSelector: "com.atollon.gui::FolderContextSelector",
                FolderSelector: "com.atollon.gui::FolderSelector",
                controls:{
                    ItemSelectorField: "com.atollon.gui.controls::ItemSelectorField",
                    UserSelector: "com.atollon.gui.controls::UserSelector"
                }
            }
        }
    }
};
export function FormControl ({ component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {
    // console.log('FormControl', component);
    if(component.PROPERTIES.required === 'true') {
        markMandatory(component.FORMITEM);
    }
    switch(component.CLASS) {
        case CONTROLS.com.atollon.formDesigner.components.MainContentGroup:
            return (<MainContentGroup component={component} updateData={updateData} getData={getData} markMandatory={markMandatory} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.gui.FolderContextSelector:
            return (<FolderContextSelector component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.gui.FolderSelector:
            return (<FolderSelector component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.VBoxExtended:
            return (<VBoxExtended component={component} updateData={updateData} getData={getData} markMandatory={markMandatory} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.HBoxExtended:
            return (<HBoxExtended component={component} updateData={updateData} getData={getData} markMandatory={markMandatory} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormTextInput:
            return (<FormTextInput component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormDateField:
            return (<FormDateField component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormNumberInput:
            return (<FormNumberInput component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormComboBox:
            return (<FormComboBox component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormTextArea:
            return (<FormTextArea component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.formDesigner.components.inputs.FormCheckbox:
            return (<FormCheckbox component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.gui.controls.ItemSelectorField:
            return (<ItemSelectorField component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.com.atollon.gui.controls.UserSelector:
            return (<UserSelector component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.mx.controls.Label:
            return (<FormLabel component={component} updateData={updateData} getData={getData} />);
        case CONTROLS.mx.controls.List:
            return (<FormList component={component} updateData={updateData} getData={getData} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        case CONTROLS.mx.containers.Canvas:
            return (<Canvas component={component} updateData={updateData} getData={getData} markMandatory={markMandatory} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
        default:
            return (<UnSupportedComponent component={component} updateData={updateData} getData={getData} markMandatory={markMandatory} onFieldFocus={onFieldFocus} onFieldBlur={onFieldBlur} />);
    }
}

function MainContentGroup( { component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {

    return (
        <View>
            {component.CHILDREN && component.CHILDREN.COMPONENT.map((child, index) => (
                <FormControl
                    key={index}
                    component={child}
                    updateData={updateData}
                    getData={getData}
                    markMandatory={markMandatory}
                    onFieldFocus={onFieldFocus}
                    onFieldBlur={onFieldBlur}
                />
            ))}
        </View>
    );
}

function FolderSelector( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    const handleValueChange = (value: any) => {
        updateData(component.FORMITEM, value);
    }
    return (
        <View>
            <div
                onFocusCapture={() => onFieldFocus?.(component.FORMITEM)}
                onBlurCapture={() => onFieldBlur?.(component.FORMITEM)}
            >
                <FolderDropDown 
                    compKey={component.FORMITEM} 
                    label={component.PROPERTIES.name || component.PROPERTIES.caption || 'Folder'} 
                    width={'100%'} 
                    value={getData(component.FORMITEM, '')}
                    isRequired={false} 
                    flatView={false}
                    folder_type_id={component.PROPERTIES.folderTypes ? parseInt(component.PROPERTIES.folderTypes) : undefined}
                    onValueChange={handleValueChange}
                    onBlur={() => onFieldBlur?.(component.FORMITEM)}
                />            
            </div>
        </View>
    );
}

function FolderContextSelector( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    const handleValueChange = (value: any) => {
        updateData(component.FORMITEM, value);
    }
    return (
        <View>
            <div
                onFocusCapture={() => onFieldFocus?.(component.FORMITEM)}
                onBlurCapture={() => onFieldBlur?.(component.FORMITEM)}
            >
                <FolderDropDown 
                    compKey={component.FORMITEM} 
                    label={component.PROPERTIES.name || component.PROPERTIES.caption || 'Folder'} 
                    width={'100%'} 
                    value={getData(component.FORMITEM, '')}
                    isRequired={false} 
                    flatView={false}
                    folder_type_id={component.PROPERTIES.folderTypeId ? parseInt(component.PROPERTIES.folderTypeId) : undefined}
                    onValueChange={handleValueChange}
                    onBlur={() => onFieldBlur?.(component.FORMITEM)}
                />            
            </div>
        </View>
    );
}

function VBoxExtended( { component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {
    var modified_class = {
        opacity: component.PROPERTIES.alpha ? +component.PROPERTIES.alpha : 1,
        padding: component.PROPERTIES.removePadding === 'true' ? '0px' : '18px'
    }

    return (
        <Flex direction={'column'} gap={10}>
            {(component.PROPERTIES.showCaption && component.PROPERTIES.showCaption === 'true') && <Text>{component.PROPERTIES.caption}</Text>}
            <Flex direction="column" gap={10} UNSAFE_className={styles.containerControl} UNSAFE_style={modified_class}>
                {component.CHILDREN && component.CHILDREN.COMPONENT.map((child, index) => (
                    <FormControl
                        key={index}
                        component={child}
                        updateData={updateData}
                        getData={getData}
                        markMandatory={markMandatory}
                        onFieldFocus={onFieldFocus}
                        onFieldBlur={onFieldBlur}
                    />
                ))}
            </Flex>
        </Flex>
    );
}

function Canvas( { component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {

    return (
        <View>
            {component.CHILDREN && component.CHILDREN.COMPONENT.map((child, index) => (
                <FormControl
                    key={index}
                    component={child}
                    updateData={updateData}
                    getData={getData}
                    markMandatory={markMandatory}
                    onFieldFocus={onFieldFocus}
                    onFieldBlur={onFieldBlur}
                />
            ))}
        </View>
    );
}

function HBoxExtended( { component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {
    var modified_class = {
        opacity: component.PROPERTIES.alpha ? +component.PROPERTIES.alpha : 1,
        padding: component.PROPERTIES.removePadding === 'true' ? '0px' : '18px'
    }
    return (
        <Flex direction={'column'} gap={10}>
            {(component.PROPERTIES.showCaption && component.PROPERTIES.showCaption === 'true') && <Text>{component.PROPERTIES.caption}</Text>}
            <Flex direction={{ "base": 'column', "M": "row"}} gap={10} UNSAFE_className={styles.containerControl} UNSAFE_style={modified_class}>
                {component.CHILDREN && component.CHILDREN.COMPONENT.map((child, index) => (
                    <FormControl
                        key={index}
                        component={child}
                        updateData={updateData}
                        getData={getData}
                        markMandatory={markMandatory}
                        onFieldFocus={onFieldFocus}
                        onFieldBlur={onFieldBlur}
                    />
                ))}
            </Flex>
        </Flex>
    );
}

function UnSupportedComponent( { component, updateData, getData, markMandatory, onFieldFocus, onFieldBlur }: IFormControlMarkMandatoryProps) {
    return (
        <View>
            <div style={warning_styles}>UnSupportedComponent: {component.CLASS}</div>
            {component.CHILDREN && component.CHILDREN.COMPONENT.map((child, index) => (
                <FormControl
                    key={index}
                    component={child}
                    updateData={updateData}
                    getData={getData}
                    markMandatory={markMandatory}
                    onFieldFocus={onFieldFocus}
                    onFieldBlur={onFieldBlur}
                />
            ))}
        </View>
    );
}

function FormTextInput( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    var data = getData(component.FORMITEM, '');

    return (
        <View>
            <TextField label={component.PROPERTIES.name || component.PROPERTIES.caption} 
                width={'100%'}
                value={data} 
                defaultValue={component.PROPERTIES.value}
                onChange={(value) => updateData(component.FORMITEM, value)}
                onFocus={() => onFieldFocus?.(component.FORMITEM)}
                onBlur={() => onFieldBlur?.(component.FORMITEM)}
                aria-label='none'
                isRequired={component.PROPERTIES.required === 'true'}
                />
        </View>
    );
}

function FormDateField( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    var data = getData(component.FORMITEM, null);
    // Add type checking before string operations
    if(data !== null && data !== undefined && typeof data === 'string' && data.length > 0){
        if(data.charAt(data.length-1) !== 'Z')
            data = data+'Z';
        if(data.charAt(10) !== 'T')
            data = data.substring(0, 10)+'T'+data.substring(11);
        data = parseAbsoluteToLocal(data);
    } else {
        data = null;
    }

    const handleChanged = (value: any) => {
        var selected_date = new Date(value.year, value.month - 1, value.day);
        updateData(component.FORMITEM, value === null ? null : SoapUtils.toDateForXml(selected_date, true));
    }
    return (
        <View>
            <DatePicker label={component.PROPERTIES.name || component.PROPERTIES.caption} 
                        width={'100%'}
                        granularity="day" 
                        defaultValue={parseAbsoluteToLocal(component.PROPERTIES.value ? component.PROPERTIES.value+'Z': new Date().toISOString())}
                        onChange={handleChanged}
                        onFocusChange={(isFocused) => {
                            if(isFocused) onFieldFocus?.(component.FORMITEM);
                            else onFieldBlur?.(component.FORMITEM);
                        }}
                        value={data}
                        aria-label='none'
                        isRequired={component.PROPERTIES.required === 'true'}
                        />
        </View>
    );
}

function FormNumberInput( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    return (
        <View>
            <NumberField label={component.PROPERTIES.name || component.PROPERTIES.caption} 
                        width={'100%'}
                        value={+(getData(component.FORMITEM, 0))} 
                        defaultValue={+component.PROPERTIES.value!} 
                        onChange={(value) => updateData(component.FORMITEM, value)}
                        onFocus={() => onFieldFocus?.(component.FORMITEM)}
                        onBlur={() => onFieldBlur?.(component.FORMITEM)}
                        aria-label='none'
                        isRequired={component.PROPERTIES.required === 'true'}
                        />
        </View>
    );
}

function FormComboBox( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    var dataProvider = component.PROPERTIES.dataProvider;
    
    if(dataProvider === undefined
        || dataProvider!.items === undefined
        || dataProvider!.items!.item === undefined){
        return (<View UNSAFE_style={warning_styles}>FormComboBox: No dataProvider.items.item</View>);
    }
    var items = Array.isArray(dataProvider!.items!.item) ? dataProvider!.items!.item : [dataProvider!.items!.item];
    return (
        <View>
            <ComboBox label={component.PROPERTIES.name || component.PROPERTIES.caption} 
                        width={'100%'}
                        defaultSelectedKey={component.PROPERTIES.value}
                        selectedKey={getData(component.FORMITEM, '')} 
                        onSelectionChange={(key) => updateData(component.FORMITEM, key)}
                        onFocus={() => onFieldFocus?.(component.FORMITEM)}
                        onBlur={() => onFieldBlur?.(component.FORMITEM)}
                        aria-label='none'
                        isRequired={component.PROPERTIES.required === 'true'}
                        >
                {(items.map((v, i) => (<Item key={v}>{v}</Item>)))}
            </ComboBox>
        </View>
    );
}

function FormTextArea( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    return (
        <View>
            <TextArea label={component.PROPERTIES.name || component.PROPERTIES.caption} 
                        width={'100%'}
                        value={getData(component.FORMITEM, '')} 
                        defaultValue={component.PROPERTIES.value} 
                        onChange={(value) => updateData(component.FORMITEM, value)}
                        onFocus={() => onFieldFocus?.(component.FORMITEM)}
                        onBlur={() => onFieldBlur?.(component.FORMITEM)}
                        aria-label='none'
                        isRequired={component.PROPERTIES.required === 'true'}
                        />
        </View>
    );
}

function FormLabel({ component, updateData, getData }: IFormControlProps){
    var labelStyles = {
        color: component.STYLES.color,
        fontSize: `${component.STYLES.fontSize}px`,
        fontWeight: component.STYLES.fontWeight
    }
    return (
        <View>
            <div style={labelStyles}>{component.PROPERTIES.text}</div> 
        </View>
    )
}

function FormCheckbox( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    var val = getData(component.FORMITEM, false);
    return (
        <View>
            <Checkbox isSelected={!!(val === 'true' || val === true)}
                        onChange={(value) => updateData(component.FORMITEM, value)}
                        onFocus={() => onFieldFocus?.(component.FORMITEM)}
                        onBlur={() => onFieldBlur?.(component.FORMITEM)}
                        aria-label='none'
                >
                {component.PROPERTIES.label}
            </Checkbox>
        </View>
    );
}

function FormList( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    var list_item = component.PROPERTIES.dataProvider!.items.item as string[];
    var selected_item = new Set<string>(getData(component.FORMITEM, '').split(','));
    return (
        <View>
            <div>{component.PROPERTIES.name || component.PROPERTIES.caption}</div>
            <div
                onFocusCapture={() => onFieldFocus?.(component.FORMITEM)}
                onBlurCapture={() => onFieldBlur?.(component.FORMITEM)}
            >
                <ListView selectionMode='single'
                            selectionStyle='highlight'
                            aria-label='none'
                            defaultSelectedKeys={component.PROPERTIES.value}
                            selectedKeys={selected_item}
                            onSelectionChange={(key:any) => updateData(component.FORMITEM, key.currentKey)}
                    >
                    {list_item.map((v, i) => (<Item key={v}>{v}</Item>))}
                </ListView>
            </div>
        </View>
    );
}
function ItemSelectorField( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    return (
        <View>
            <div
                onFocusCapture={() => onFieldFocus?.(component.FORMITEM)}
                onBlurCapture={() => onFieldBlur?.(component.FORMITEM)}
            >
                <ItemDropDownFormControl component={component} updateData={updateData} getData={getData} />
            </div>
        </View>
    );
}
function UserSelector( { component, updateData, getData, onFieldFocus, onFieldBlur }: IFormControlProps) {
    return (
        <View>
            <div
                onFocusCapture={() => onFieldFocus?.(component.FORMITEM)}
                onBlurCapture={() => onFieldBlur?.(component.FORMITEM)}
            >
                <UserDropDownFormControl component={component} updateData={updateData} getData={getData} />
            </div>
        </View>
    );
}
